/* eslint-disable @typescript-eslint/no-misused-promises */
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Alert, Autocomplete, Box, Chip, CircularProgress, Divider, InputLabel, Link, Stack, TextField, Typography } from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { useMutation, useQuery } from '@tanstack/react-query'
import { FormProvider, useForm, Controller } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import GenericModal from '../../../../../components/GenericModal'
import DefaultModalActions from '../../../../../components/GenericModal/DefaultModalActions'
import GenericDateTimePicker from '../../../../../components/GenericDateTimePicker'
import CustomTooltip from '../../../../../components/CustomTooltip'
import { DistributionManager } from '../../../../../APIManager'
import { SandboxSeriesFilter } from '../../../../../APIManager/DistributionManager'
import { type ISection } from '../../../../../models/TermsSection'
import { SlotStatus } from '../../../../../models/DistributionPlan'
import { chipColors } from '../../../../ProjectCourseFeature/CourseEvaluationHeader'
import { viewFromStatus } from '../../../../ProjectCourseFeature/ProjectSectionTab'
import { type ApiError } from '../../../../../models/Common'
import { notification } from '../../../../../components/Notifications'
import { formatDateWithTime } from '../../../../../helpers/formatDateWithTime'
import { formatDateByUserTimeZone } from '../../../../../helpers/formatDateByUserTimeZone'
import { parseReleaseTimestamp, ReleaseMode } from '../../../../../helpers/parseReleaseTimestamp'
import { blue, grey } from '../../../../../colors'
import ROUTES from '../../../../../consts/paths'
import { type DataObject } from '../../../../../models/DataObject'
import { type IDistributionSeries } from '../../../../../models/DistributionSeries'
import { isInstructorReleaseOnGradePostingEnabled } from '../../../../../consts/messages'
import { useSlotBackgroundTask } from '../../../../../hooks/useSlotBackgroundTask'
import { notifySlotTaskTerminal } from '../../../../../utils/notifySlotTaskTerminal'
import { getEnqueuedTaskIds } from '../../../../../utils/enqueuedTaskIds'

interface AddToSurveyProjectModalProps {
  isOpen: boolean
  onClose: () => void
  selectedRow?: ISection | Record<string, any>
  termId: string
  readonly?: boolean
  onRefresh: () => Promise<void>
  onParentClose: () => void
}

interface SurveyProjectOption {
  slotId: string
  slotTitle: string
  distributionSeriesId: string
  distributionSeriesTitle: string
  slotStatus: SlotStatus
  termId: string
  slotStartTimestamp: string
  slotEndTimestamp: string
  releaseTimestamp: string | Date
  isRollingResultsEnabled: boolean
  isAlreadyLinked: boolean
  isClosed: boolean
  isDisabled: boolean
  isInstructorReleaseOnGradePostingEnabled?: boolean
}

interface FormValues {
  slotId: string
  startTimestamp: Date | null
  endTimestamp: Date | null
  earlyAccessReleaseTimestamp1: Date | null
  earlyAccessReleaseTimestamp2: Date | null
}

interface SurveyProjectSlotResponse {
  slotId: string
  term: {
    termId: string
  }
  slotNodeDetails: {
    slotStatus: SlotStatus
  }
}

interface SurveyProjectSlotDetailResponse extends SurveyProjectSlotResponse {
  title: string
  slotStartTimestamp: string
  slotEndTimestamp: string
  releaseTimestamp: string | Date
  isRollingResultsEnabled?: boolean
  isArchived?: boolean
  isInstructorReleaseOnGradePostingEnabled?: boolean
}

interface SurveyProjectSlotGroup {
  seriesTitle: string
  distributionSeriesId: string
  slots: SurveyProjectSlotResponse[]
}

interface SurveyProjectSlotResponseEnvelope {
  data?: SurveyProjectSlotResponse[]
}

interface SurveyProjectSlotCandidate {
  slotId: string
  slotStatus: SlotStatus
  termId: string
  distributionSeriesId: string
  seriesTitle: string
}

interface SurveyProjectSlotDetailEnvelope {
  data?: SurveyProjectSlotDetailResponse
}

const DEFAULT_VALUES: FormValues = {
  slotId: '',
  startTimestamp: null,
  endTimestamp: null,
  earlyAccessReleaseTimestamp1: null,
  earlyAccessReleaseTimestamp2: null
}

const getSelectedSectionId = (selectedRow?: ISection | Record<string, any>): string => {
  return selectedRow?.sectionId ?? selectedRow?.linkedSection?.sectionId ?? ''
}

const formatProjectDate = (value?: string | Date | null): string => {
  if (!value) return 'Not set'
  return formatDateByUserTimeZone(value)
}

const formatDropdownProjectDate = (value?: string | Date | null): string => {
  if (!value) return 'Not set'
  return formatDateByUserTimeZone(value, 'MM/dd/yyyy hh:mm a')
}

const getNormalizedTermId = (value?: string | number | null): string => {
  return value == null ? '' : String(value)
}

// The distribution-series list has returned term ids in multiple shapes across environments.
// Normalize both primitive ids and object-wrapped ids before filtering by the active term.
const getDistributionSeriesTermId = (term: string | number | { termId?: string | number } | null | undefined): string => {
  if (term != null && typeof term === 'object' && 'termId' in term) {
    return getNormalizedTermId(term.termId)
  }

  return getNormalizedTermId(typeof term === 'string' || typeof term === 'number' ? term : null)
}

// The slots endpoint is inconsistent across call sites and can return either a bare array or
// an envelope with `data`, so normalize it once before flattening candidates.
const getSlotResponseList = (value: SurveyProjectSlotResponse[] | SurveyProjectSlotResponseEnvelope | null | undefined): SurveyProjectSlotResponse[] => {
  if (Array.isArray(value)) {
    return value
  }

  if (value != null && 'data' in value && Array.isArray(value.data)) {
    return value.data
  }

  return []
}

// Slot hydration follows the same pattern as the summary list and may arrive as either a direct
// slot object or an object-wrapped payload.
const getSlotDetail = (
  value: SurveyProjectSlotDetailResponse | SurveyProjectSlotDetailEnvelope | null | undefined
): SurveyProjectSlotDetailResponse | null => {
  if (value == null) {
    return null
  }

  if ('slotId' in value) {
    return value
  }

  if ('data' in value && value.data != null) {
    return value.data
  }

  return null
}

const buildSurveyProjectSectionUrl = (slotId: string, slotStatus: SlotStatus, sectionSearch?: string): string => {
  const params = new URLSearchParams({
    view: viewFromStatus(slotStatus)
  })

  if (sectionSearch != null && sectionSearch.trim().length > 0) {
    params.set('search', sectionSearch)
  }

  return `${ROUTES.COURSE_AND_ENROLLMENT}/${slotId}?${params.toString()}`
}

const getDisabledOptionHelp = (option: SurveyProjectOption): React.ReactNode => {
  if (option.isAlreadyLinked && option.isClosed) {
    return (
      <Box>
        <Typography variant="body2">
          This Course Section already exists in this Survey Project.
        </Typography>
      </Box>
    )
  }

  if (option.isAlreadyLinked) {
    return (
      <Box>
        <Typography variant="body2">
          This Course Section already exists in this Survey Project.
        </Typography>
      </Box>
    )
  }

  if (option.isClosed) {
    return (
      <Box>
        <Typography variant="body2">
          This Survey Project is closed and cannot accept new Course Sections.
        </Typography>
        <Typography variant="body2" mt={1}>
          Reopen it from the Survey Project page if you need to make changes there.
        </Typography>
      </Box>
    )
  }

  return null
}

const compareSurveyProjectOptions = (left: SurveyProjectOption, right: SurveyProjectOption): number => {
  const leftStart = new Date(left.slotStartTimestamp).getTime()
  const rightStart = new Date(right.slotStartTimestamp).getTime()

  if (leftStart !== rightStart) {
    return leftStart - rightStart
  }

  return left.slotTitle.localeCompare(right.slotTitle)
}

const buildValidationSchema = (
  selectedProjectRef: React.MutableRefObject<SurveyProjectOption | null>
): yup.AnyObjectSchema => {
  const getProject = (): SurveyProjectOption | null => selectedProjectRef.current

  const getProjectReleaseDate = (): Date | null => {
    const project = getProject()
    if (!project || project.isRollingResultsEnabled || project.releaseTimestamp === ReleaseMode.Rolling) {
      return null
    }
    return parseReleaseTimestamp(project.releaseTimestamp)
  }

  return yup.object({
    slotId: yup.string().required('Survey Project is required'),
    startTimestamp: yup
      .date()
      .nullable()
      .typeError('Start Date is required')
      .required('Start Date is required')
      .test(
        'start-after-project-start',
        'Start Date must be on or after the Survey Project start date',
        (value) => {
          const project = getProject()
          if (!project || !value) return true
          return value.getTime() >= new Date(project.slotStartTimestamp).getTime()
        }
      )
      .test(
        'start-before-project-end',
        'Start Date must be on or before the Survey Project end date',
        (value) => {
          const project = getProject()
          if (!project || !value) return true
          return value.getTime() <= new Date(project.slotEndTimestamp).getTime()
        }
      ),
    endTimestamp: yup
      .date()
      .nullable()
      .typeError('End Date is required')
      .required('End Date is required')
      .test(
        'end-after-start',
        'End Date must be on or after Start Date',
        function (value) {
          if (!value) return true
          const { startTimestamp } = this.parent
          if (!startTimestamp) return true
          return value.getTime() >= new Date(startTimestamp).getTime()
        }
      )
      .test(
        'end-before-project-end',
        'End Date must be on or before the Survey Project end date',
        (value) => {
          const project = getProject()
          if (!project || !value) return true
          return value.getTime() <= new Date(project.slotEndTimestamp).getTime()
        }
      ),
    earlyAccessReleaseTimestamp1: yup
      .date()
      .nullable()
      .typeError('Instructor Release Date is required')
      .test(
        'instructor-release-required',
        'Instructor Release Date is required',
        (value) => {
          const project = getProject()
          if (!project || project.isRollingResultsEnabled) return true
          if (isInstructorReleaseOnGradePostingEnabled(project.isInstructorReleaseOnGradePostingEnabled)) return true
          return value instanceof Date && !isNaN(value.getTime())
        }
      )
      .test(
        'instructor-release-after-end',
        'Instructor Release Date must be on or after End Date',
        function (value) {
          const project = getProject()
          if (!project || project.isRollingResultsEnabled || !value) return true
          const { endTimestamp } = this.parent
          if (!endTimestamp) return true
          return value.getTime() >= new Date(endTimestamp).getTime()
        }
      )
      .test(
        'instructor-release-before-project-release',
        'Instructor Release Date must be on or before the Survey Project release date',
        (value) => {
          const releaseDate = getProjectReleaseDate()
          if (!releaseDate || !value) return true
          return value.getTime() <= releaseDate.getTime()
        }
      ),
    earlyAccessReleaseTimestamp2: yup
      .date()
      .nullable()
      .typeError('Admin Release Date is required')
      .test(
        'admin-release-required',
        'Admin Release Date is required',
        (value) => {
          const project = getProject()
          if (!project || project.isRollingResultsEnabled) return true
          return value instanceof Date && !isNaN(value.getTime())
        }
      )
      .test(
        'admin-release-after-end',
        'Admin Release Date must be on or after End Date',
        function (value) {
          const project = getProject()
          if (!project || project.isRollingResultsEnabled || !value) return true
          const { endTimestamp } = this.parent
          if (!endTimestamp) return true
          return value.getTime() >= new Date(endTimestamp).getTime()
        }
      )
      .test(
        'admin-release-before-project-release',
        'Admin Release Date must be on or before the Survey Project release date',
        (value) => {
          const releaseDate = getProjectReleaseDate()
          if (!releaseDate || !value) return true
          return value.getTime() <= releaseDate.getTime()
        }
      )
  })
}

const AddToSurveyProjectModal: React.FC<AddToSurveyProjectModalProps> = ({
  isOpen,
  onClose,
  selectedRow,
  termId,
  readonly = false,
  onRefresh,
  onParentClose
}) => {
  const selectedSectionId = getSelectedSectionId(selectedRow)
  const linkedSlotIds = useMemo(() => {
    return new Set<string>(
      (selectedRow?.distributionDetails ?? [])
        .map((distribution: { slotId?: string | null }) => distribution?.slotId)
        .filter((slotId: string | null | undefined): slotId is string => Boolean(slotId))
    )
  }, [selectedRow?.distributionDetails])
  const linkedSlotIdsKey = useMemo(
    () => Array.from(linkedSlotIds).sort((left, right) => left.localeCompare(right)).join(','),
    [linkedSlotIds]
  )

  const selectedProjectRef = useRef<SurveyProjectOption | null>(null)
  const [selectedProject, setSelectedProject] = useState<SurveyProjectOption | null>(null)
  const validationSchema = useMemo(() => buildValidationSchema(selectedProjectRef), [])
  // Keyed by the target slot (the survey project being added to) — real slotId, known once
  // selected. Tracked in its own ref rather than reading `selectedProject` directly: this modal
  // never unmounts (its parent always renders it, only `isOpen` toggles), and the close effect
  // below resets `selectedProject` to null on every close — reading it live would drop this
  // hook's tracking key right after `setTask` fires (handleAddSection calls onClose()
  // immediately after a successful submit), orphaning the just-created task from this
  // component's own poller and leaving GlobalSlotTaskPoller to claim it under a key this
  // instance no longer recognizes as its own.
  const taskTrackingSlotIdRef = useRef<string>('')
  const { setTask } = useSlotBackgroundTask({ slotId: taskTrackingSlotIdRef.current, onTerminal: notifySlotTaskTerminal })

  const methods = useForm<any>({
    resolver: yupResolver(validationSchema),
    defaultValues: DEFAULT_VALUES,
    mode: 'onChange'
  })

  const {
    control,
    handleSubmit,
    reset,
    clearErrors,
    watch,
    trigger,
    formState: { isSubmitting, isValid }
  } = methods

  const startTimestamp = watch('startTimestamp')
  const endTimestamp = watch('endTimestamp')

  useEffect(() => {
    if (!isOpen) {
      setSelectedProject(null)
      selectedProjectRef.current = null
      reset(DEFAULT_VALUES)
    }
  }, [isOpen, reset])

  const {
    data: surveyProjectOptions = [],
    isLoading: isLoadingSurveyProjects,
    isFetching: isFetchingSurveyProjects,
    isError: isSurveyProjectsError
  } = useQuery<SurveyProjectOption[]>({
    queryKey: ['term-survey-project-options', termId, selectedSectionId, linkedSlotIdsKey],
    enabled: isOpen && Boolean(termId),
    staleTime: 60_000,
    meta: { silent: true },
    queryFn: async (): Promise<SurveyProjectOption[]> => {
      const normalizedTermId = getNormalizedTermId(termId)
      // CR-341: `sandbox=include` because the backend now defaults this endpoint to real projects
      // only, which would drop a project whose every term is a rehearsal out of this picker — and
      // adding sections to the project being rehearsed is the point of a rehearsal. Not a
      // permission: the backend's visibility filter runs first, so a caller without a Global-level
      // role still sees no rehearsals here.
      const distributionSeriesResponse = await DistributionManager.getAllDistributionSeries({ sandbox: SandboxSeriesFilter.Include }, { isPublic: true })
      const seriesFlagById = new Map<string, boolean>(
        distributionSeriesResponse.data.map((series: DataObject<IDistributionSeries>) => [
          series.data.distributionSeriesId,
          isInstructorReleaseOnGradePostingEnabled(series.data.isInstructorReleaseOnGradePostingEnabled)
        ])
      )
      const matchingSeries = distributionSeriesResponse.data.filter((series: DataObject<IDistributionSeries>) =>
        series?.data?.terms?.some((term: string | number | { termId?: string | number }) => getDistributionSeriesTermId(term) === normalizedTermId)
      )

      if (matchingSeries.length === 0) {
        return []
      }

      // Load slot summaries first to keep the initial term filter cheap, then hydrate only the
      // matching term slots because the summary response omits the display dates needed by the UI.
      const slotResponses = await Promise.allSettled(
        matchingSeries.map(async (series: DataObject<IDistributionSeries>) => {
          const response = await DistributionManager.getSlotsForDistributionSeriesBySeriesId(series.data.distributionSeriesId)
          return {
            seriesTitle: series.data.title,
            distributionSeriesId: series.data.distributionSeriesId,
            slots: getSlotResponseList(response.data as SurveyProjectSlotResponse[] | SurveyProjectSlotResponseEnvelope | undefined)
          }
        })
      )

      const slotCandidates = slotResponses
        .filter((result): result is PromiseFulfilledResult<SurveyProjectSlotGroup> => result.status === 'fulfilled')
        .map(result => result.value)
        .flatMap(({ seriesTitle, distributionSeriesId, slots }: SurveyProjectSlotGroup) => {
          return slots
            .filter((slot: SurveyProjectSlotResponse) => {
              return getNormalizedTermId(slot?.term?.termId) === normalizedTermId && slot?.slotNodeDetails?.slotStatus != null
            })
            .map((slot: SurveyProjectSlotResponse): SurveyProjectSlotCandidate => ({
              slotId: slot.slotId,
              slotStatus: slot.slotNodeDetails.slotStatus,
              termId: slot.term.termId,
              distributionSeriesId,
              seriesTitle
            }))
        })

      const hydratedSlots = await Promise.allSettled(
        slotCandidates.map(async (candidate: SurveyProjectSlotCandidate) => {
          const response = await DistributionManager.getSlot(candidate.slotId)
          return {
            candidate,
            slot: getSlotDetail(response.data as SurveyProjectSlotDetailResponse | SurveyProjectSlotDetailEnvelope | undefined)
          }
        })
      )

      return hydratedSlots
        .flatMap((result): SurveyProjectOption[] => {
          if (result.status !== 'fulfilled' || result.value.slot == null) {
            return []
          }

          const { candidate, slot } = result.value
          if (slot.isArchived) {
            return []
          }

          const resolvedSlotStatus = slot.slotNodeDetails.slotStatus ?? candidate.slotStatus

          return [{
            slotStatus: resolvedSlotStatus,
            isClosed: resolvedSlotStatus === SlotStatus.Closed,
            isAlreadyLinked: linkedSlotIds.has(slot.slotId),
            isDisabled: linkedSlotIds.has(slot.slotId) || resolvedSlotStatus === SlotStatus.Closed,
            slotId: slot.slotId,
            slotTitle: slot.title,
            distributionSeriesId: candidate.distributionSeriesId,
            distributionSeriesTitle: candidate.seriesTitle,
            termId: slot.term.termId,
            slotStartTimestamp: slot.slotStartTimestamp,
            slotEndTimestamp: slot.slotEndTimestamp,
            releaseTimestamp: slot.releaseTimestamp,
            isRollingResultsEnabled: Boolean(slot.isRollingResultsEnabled || slot.releaseTimestamp === ReleaseMode.Rolling),
            isInstructorReleaseOnGradePostingEnabled: isInstructorReleaseOnGradePostingEnabled(
              slot.isInstructorReleaseOnGradePostingEnabled ?? seriesFlagById.get(candidate.distributionSeriesId)
            )
          }]
        })
        .sort(compareSurveyProjectOptions)
    }
  })

  const { mutateAsync: addSectionToSurveyProject, isPending: isAddPending } = useMutation({
    mutationFn: async ({ slotId, payload }: { slotId: string, payload: Record<string, any> }) => {
      return await DistributionManager.addSectionToSurveyProject(slotId, payload)
    },
    meta: { silent: true }
  })

  const handleProjectSelection = async (project: SurveyProjectOption | null): Promise<void> => {
    if (project?.isDisabled) {
      return
    }

    setSelectedProject(project)
    selectedProjectRef.current = project
    if (project?.slotId) taskTrackingSlotIdRef.current = project.slotId
    clearErrors()

    if (!project) {
      reset(DEFAULT_VALUES)
      return
    }

    const defaultReleaseDate = project.isRollingResultsEnabled ? null : parseReleaseTimestamp(project.releaseTimestamp)
    const isGradePostingReleaseEnabled = isInstructorReleaseOnGradePostingEnabled(project.isInstructorReleaseOnGradePostingEnabled)

    reset({
      slotId: project.slotId,
      startTimestamp: new Date(project.slotStartTimestamp),
      endTimestamp: new Date(project.slotEndTimestamp),
      earlyAccessReleaseTimestamp1: isGradePostingReleaseEnabled ? null : defaultReleaseDate,
      earlyAccessReleaseTimestamp2: defaultReleaseDate
    })

    await trigger()
  }

  const handleAddSection = async (values: FormValues): Promise<void> => {
    const sectionId = getSelectedSectionId(selectedRow)
    const sectionAbbreviation = String(selectedRow?.abbreviation ?? '').trim()

    if (!selectedProject || !sectionId) {
      notification('Unable to determine the Course Section or Survey Project.', { variant: 'error' })
      return
    }

    const payload: Record<string, any> = {
      sectionId,
      startTimestamp: formatDateWithTime(values.startTimestamp ?? ''),
      endTimestamp: formatDateWithTime(values.endTimestamp ?? '')
    }

    if (!selectedProject.isRollingResultsEnabled) {
      if (isInstructorReleaseOnGradePostingEnabled(selectedProject.isInstructorReleaseOnGradePostingEnabled)) {
        // Grade-posting: instructor release (_2) is optional. Send explicit null when left blank
        // so the backend unsets it rather than inheriting a slot-level fallback — matching the
        // CreateAndEditCourses pattern. Admin release (_1) remains required by validation.
        payload.earlyAccessReleaseTimestamp_2 = values.earlyAccessReleaseTimestamp1
          ? formatDateWithTime(values.earlyAccessReleaseTimestamp1)
          : null
        payload.earlyAccessReleaseTimestamp_1 = formatDateWithTime(values.earlyAccessReleaseTimestamp2 ?? '')
      } else {
        // Flag OFF: both dates are required — preserve the original always-send behavior exactly.
        payload.earlyAccessReleaseTimestamp_2 = formatDateWithTime(values.earlyAccessReleaseTimestamp1 ?? '')
        payload.earlyAccessReleaseTimestamp_1 = formatDateWithTime(values.earlyAccessReleaseTimestamp2 ?? '')
      }
    }

    try {
      const response = await addSectionToSurveyProject({
        slotId: selectedProject.slotId,
        payload
      })
      const seedTaskIds = getEnqueuedTaskIds(response)
      if (seedTaskIds.length > 0) {
        // Every id, not just the first: a project with both default question sets attached seeds
        // each of them, and the queue reports once the last one lands. Copy stays kind-agnostic
        // because nothing in the response says which seed an id belongs to.
        setTask(
          seedTaskIds,
          response?.data?.message ?? 'Survey questions are being prepared in the background.',
          'Survey questions are ready.'
        )
      }
      const surveyProjectUrl = buildSurveyProjectSectionUrl(selectedProject.slotId, selectedProject.slotStatus, sectionAbbreviation)

      notification(
        `${sectionAbbreviation || 'Course Section'} was added to ${selectedProject.slotTitle} successfully.`,
        {
          variant: 'success',
          subTitle: (
            <Stack spacing={1}>
              <Link
                component="button"
                type="button"
                color="inherit"
                underline="always"
                sx={{ width: 'fit-content', fontSize: '14px', fontWeight: 600 }}
                onClick={() => {
                  const openedWindow = window.open(surveyProjectUrl, '_blank', 'noopener,noreferrer')

                  if (openedWindow != null) {
                    openedWindow.opener = null
                  }
                }}
              >
                Open Survey Project
              </Link>
            </Stack>
          )
        }
      )
      await onRefresh()
      onClose()
      onParentClose()
    } catch (error) {
      const customError = error as ApiError
      notification(customError.message || 'Failed to add Course Section to Survey Project.', { variant: 'error' })
    }
  }

  const hasSelectableSurveyProjects = surveyProjectOptions.some(project => !project.isDisabled)
  const noEligibleSurveyProjects = !isLoadingSurveyProjects && !isFetchingSurveyProjects && !hasSelectableSurveyProjects
  const isGradePostingReleaseEnabled = isInstructorReleaseOnGradePostingEnabled(selectedProject?.isInstructorReleaseOnGradePostingEnabled)

  const selectedProjectReleaseLabel = selectedProject?.isRollingResultsEnabled
    ? ReleaseMode.Rolling
    : formatProjectDate(selectedProject?.releaseTimestamp)

  return (
    <GenericModal
      isOpen={isOpen}
      title="Add to Survey Project"
      onClose={onClose}
      maxWidth="sm"
      action={false}
    >
      <FormProvider {...methods}>
        <Stack spacing={3}>
            {isLoadingSurveyProjects || isFetchingSurveyProjects
              ? (
                <Box display="flex" justifyContent="center" py={6}>
                  <CircularProgress />
                </Box>
                )
              : (
                <>
                  {isSurveyProjectsError && (
                    <Alert severity="error">
                      Unable to load Survey Projects for this term. Try again.
                    </Alert>
                  )}

                  {noEligibleSurveyProjects && (
                    <Alert severity="info">
                      No eligible Survey Projects are available for this term.
                    </Alert>
                  )}

                  <Controller
                    name="slotId"
                    control={control}
                    render={({ fieldState: { error } }) => (
                      <Box>
                        <InputLabel required sx={{ mb: 2 }}>Survey Project</InputLabel>
                        <Autocomplete
                          value={selectedProject}
                          options={surveyProjectOptions}
                          isOptionEqualToValue={(option, value) => option.slotId === value.slotId}
                          getOptionLabel={(option) => option.slotTitle}
                          getOptionDisabled={(option) => option.isDisabled}
                          onChange={(_event, value) => {
                            if (value?.isDisabled) {
                              return
                            }
                            void handleProjectSelection(value)
                          }}
                          renderOption={(props, option) => {
                            // `renderOption` receives MUI-managed DOM props, not React component props.
                            // eslint-disable-next-line react/prop-types
                            const { key: _optionKey, onClick, ...optionProps } = props
                            const resultsReleaseLabel = option.isRollingResultsEnabled
                              ? ReleaseMode.Rolling
                              : formatDropdownProjectDate(option.releaseTimestamp)
                            const optionDisabledHelp = getDisabledOptionHelp(option)

                            return (
                              <li
                                key={option.slotId}
                                {...optionProps}
                                onClick={(event) => {
                                  if (option.isDisabled) {
                                    event.preventDefault()
                                    event.stopPropagation()
                                    return
                                  }
                                  onClick?.(event)
                                }}
                                data-disabled={option.isDisabled ? 'true' : 'false'}
                                style={{
                                  ...optionProps.style,
                                  opacity: option.isDisabled ? 0.55 : 1,
                                  cursor: option.isDisabled ? 'not-allowed' : 'pointer'
                                }}
                              >
                                <Box width="100%" py={1}>
                                  <Box display="flex" justifyContent="space-between" gap={2} alignItems="flex-start">
                                    <Box display="flex" flexDirection="column" gap={0.5}>
                                      <Typography fontWeight={600}>{option.slotTitle}</Typography>
                                      {option.distributionSeriesTitle !== option.slotTitle && (
                                        <Typography variant="body2" color={grey[700]}>
                                          {option.distributionSeriesTitle}
                                        </Typography>
                                      )}
                                    </Box>
                                    <Box display="flex" alignItems="center" gap={1}>
                                      <Chip
                                        size="small"
                                        variant={option.slotStatus === SlotStatus.Draft ? 'outlined' : 'filled'}
                                        color={chipColors[option.slotStatus] as any}
                                        label={option.slotStatus}
                                      />
                                      {optionDisabledHelp != null && (
                                        <CustomTooltip title={optionDisabledHelp}>
                                          <Box
                                            component="span"
                                            sx={{ display: 'inline-flex', alignItems: 'center', cursor: 'help' }}
                                            onMouseDown={(event) => {
                                              event.preventDefault()
                                              event.stopPropagation()
                                            }}
                                            onClick={(event) => {
                                              event.preventDefault()
                                              event.stopPropagation()
                                            }}
                                          >
                                            <InfoOutlinedIcon sx={{ color: blue[500], fontSize: 18, pointerEvents: 'auto' }} />
                                          </Box>
                                        </CustomTooltip>
                                      )}
                                    </Box>
                                  </Box>
                                  <Typography variant="body2" color={grey[700]} mt={1}>
                                    Survey Window: {formatDropdownProjectDate(option.slotStartTimestamp)} - {formatDropdownProjectDate(option.slotEndTimestamp)}
                                  </Typography>
                                  <Typography variant="body2" color={grey[700]}>
                                    Results Release: {resultsReleaseLabel}
                                  </Typography>
                                </Box>
                              </li>
                            )
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder="Select Survey Project"
                              error={Boolean(error)}
                              helperText={error?.message}
                            />
                          )}
                        />
                      </Box>
                    )}
                  />

                  {selectedProject && (
                    <>
                      <Divider />
                      <Alert severity="info">
                        <Typography fontWeight={600} mb={1}>
                          Survey Project Defaults
                        </Typography>
                        <Typography variant="body2">
                          Start: {formatProjectDate(selectedProject.slotStartTimestamp)}
                        </Typography>
                        <Typography variant="body2">
                          End: {formatProjectDate(selectedProject.slotEndTimestamp)}
                        </Typography>
                        <Typography variant="body2">
                          Release: {selectedProjectReleaseLabel}
                        </Typography>
                        <Typography variant="body2" mt={1}>
                          Choose Course Section dates that stay within the selected Survey Project window.
                        </Typography>
                      </Alert>

                      <GenericDateTimePicker
                        name="startTimestamp"
                        label="Start Date"
                        required
                        allowPastDates
                        minDate={new Date(selectedProject.slotStartTimestamp)}
                        maxDate={new Date(selectedProject.slotEndTimestamp)}
                        placeholder="Enter Start Date"
                      />
                      <GenericDateTimePicker
                        name="endTimestamp"
                        label="End Date"
                        required
                        allowPastDates
                        minDate={startTimestamp ?? new Date(selectedProject.slotStartTimestamp)}
                        maxDate={new Date(selectedProject.slotEndTimestamp)}
                        placeholder="Enter End Date"
                      />
                      {!selectedProject.isRollingResultsEnabled && (
                        <>
                          <GenericDateTimePicker
                            name="earlyAccessReleaseTimestamp2"
                            label="Admin Release Date"
                            required
                            allowPastDates
                            minDate={endTimestamp ?? new Date(selectedProject.slotEndTimestamp)}
                            maxDate={parseReleaseTimestamp(selectedProject.releaseTimestamp) ?? undefined}
                            placeholder="Enter Admin Release Date"
                          />
                          <GenericDateTimePicker
                            name="earlyAccessReleaseTimestamp1"
                            label="Instructor Release Date"
                            required={!isGradePostingReleaseEnabled}
                            allowPastDates
                            minDate={endTimestamp ?? new Date(selectedProject.slotEndTimestamp)}
                            maxDate={parseReleaseTimestamp(selectedProject.releaseTimestamp) ?? undefined}
                            placeholder="Enter Instructor Release Date"
                          />
                          {isGradePostingReleaseEnabled && (
                            <Typography variant="body2" color={grey[700]}>
                              Optional when grade-posting instructor release is enabled for this Survey Project.
                            </Typography>
                          )}
                        </>
                      )}
                    </>
                  )}
                </>
                )}
        </Stack>

        <DefaultModalActions
          confirmButtonProps={{
            text: 'Add',
            type: 'button',
            onClick: async () => { await handleSubmit(handleAddSection)() },
            loading: isAddPending || isSubmitting,
            disabled: readonly || noEligibleSurveyProjects || !selectedProject || Boolean(selectedProject?.isDisabled) || !isValid,
            tooltip: readonly ? "You don't have permission to perform this action. Please contact your institution admin." : undefined
          }}
          destructiveButtonProps={{
            onClick: onClose
          }}
        />
      </FormProvider>
    </GenericModal>
  )
}

export default AddToSurveyProjectModal
